/* global window, document  */
import React from 'react'
import PropTypes from 'prop-types'
import { ChevronIcon } from '@sainsburys-tech/bolt-icons'
import { withCookies } from 'react-cookie'
import ActionIcon from '../../icons/ActionIcon'
import ThreeSixtyWithTextIcon from '../../icons/ThreeSixtyWithTextIcon'
import VideoWithTextIcon from '../../icons/VideoWithTextIcon'
import SwatchIcon from '../../icons/SwatchIcon'
import Analytics from '../../helpers/media-gallery.tracking'
import config from '../../config/media-gallery.config'
import { ArkitThumbnail } from '../../../ArKit/Arkit'

import * as Styled from './Thumbnails.styles'

const noop = () => {}
const mockedEvent = { preventDefault: noop, stopPropagation: noop }
const breakpoints = {
  xs: 'only screen and (min-width: 320px)',
  sm: 'only screen and (min-width: 576px)',
  md: 'only screen and (min-width: 768px)',
  lg: 'only screen and (min-width: 992px)'
}

const STYLE_LANDSCAPE = 'landscape'
const STYLE_PORTRAIT = 'portrait'

class Thumbnails extends React.Component {
  constructor(props) {
    super(props)
    this.containerRef = React.createRef()
    const thumbnailDimensionTemplate = this.getThumbnailDimensionTemplate(props.style)

    this.state = {
      activeSlide: this.props.activeSlide,
      thumbnailDimensions: thumbnailDimensionTemplate,
      thumbnailWidth: thumbnailDimensionTemplate.desktop.thumbnail.width,
      thumbnailHeight: thumbnailDimensionTemplate.desktop.thumbnail.height,
      thumbnailSpacing: 10,
      thumbnailStyle: { top: 0, left: 0 },
      thumbnailTransition: '1s',
      showArrows: true
    }
  }

  componentDidMount() {
    const { mockWindow, onSliderLoaded, addKeyDownListener } = this.props
    const { sm, md, lg } = breakpoints

    this.screenSm = mockWindow ? mockWindow.matchMedia(sm) : window.matchMedia(sm)
    this.screenMd = mockWindow ? mockWindow.matchMedia(md) : window.matchMedia(md)
    this.screenLg = mockWindow ? mockWindow.matchMedia(lg) : window.matchMedia(lg)

    this.handleResize(mockedEvent, onSliderLoaded)

    if (addKeyDownListener) {
      document.addEventListener('keydown', this.handleKeyDown.bind(this))
    }
    window.addEventListener('resize', this.handleResize.bind(this))
    // When component initially mounts the component width from the ref is 0,
    // shouldShowArrows must be called after the component width has been established
    setTimeout(() => {
      this.setState({ showArrows: this.shouldShowArrows() })
    }, 50)
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.state.activeSlide !== nextProps.activeSlide) {
      this.setActiveSlide(nextProps.activeSlide)
    }

    if (this.props.isPortrait !== nextProps.isPortrait) {
      this.setState({ thumbnailStyle: { top: 0, left: 0 }, thumbnailTransition: 'none' })
      /*
      // When the orientation changes the thumbnailOffset would be calculated using the old component dimensions.
      // To get the correct offset the component must render first and then calculate the new thumbnail offset.
      // handleKeyDown('') causes the currently selected thumbnail to be centered.
      */
      setTimeout(() => {
        this.handleKeyDown('')
        this.setState({
          thumbnailTransition: '1s'
        })
      }, 100)
    }

    if (this.props.style !== nextProps.style) {
      const newThumbnailDimensions = this.getThumbnailDimensionTemplate(nextProps.style)
      this.setState(
        {
          thumbnailDimensions: newThumbnailDimensions,
          thumbnailWidth: this.getThumnailHeightWidth().thumbnailWidth,
          thumbnailHeight: this.getThumnailHeightWidth().thumbnailHeight
        },
        () => {
          this.setState({
            showArrows: this.shouldShowArrows(),
            thumbnailStyle: this.boundValidThumbnailStyle(this.state.thumbnailStyle, nextProps.isPortrait)
          })
        }
      )
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this))
    window.removeEventListener('resize', this.handleResize.bind(this))
  }

  getThumnailHeightWidth = () => {
    const isDesktop = this.screenSm.matches && this.screenMd.matches && this.screenLg.matches
    const isTablet = (this.screenSm.matches || this.screenMd.matches) && !this.screenLg.matches

    const deviceType = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile'

    return {
      thumbnailWidth: this.state.thumbnailDimensions[deviceType].thumbnail.width,
      thumbnailHeight: this.state.thumbnailDimensions[deviceType].thumbnail.height
    }
  }

  handleResize = (event, updateCallback) => {
    event.preventDefault()
    event.stopPropagation()

    const newThumbnailSpacing = this.screenSm.matches ? 10 : 6

    const { isPortrait } = this.props

    let style = this.state.thumbnailStyle

    if (!this.screenSm.matches) {
      style = { top: 0, left: 0 }
    } else {
      style = this.boundValidThumbnailStyle(style, isPortrait)
    }

    this.setState(
      {
        thumbnailWidth: this.getThumnailHeightWidth().thumbnailWidth,
        thumbnailHeight: this.getThumnailHeightWidth().thumbnailHeight,
        thumbnailSpacing: newThumbnailSpacing,
        thumbnailStyle: style
      },
      updateCallback
    )

    setTimeout(() => {
      this.setState({
        showArrows: this.shouldShowArrows()
      })
    }, 50)
  }

  handleKeyDown = event => {
    const { activeSlide } = this.state
    const { structuredMedia, isFocused } = this.props

    const newActiveIndex = this.getNewSlideIndex(activeSlide, event.key)
    const totalSlides = structuredMedia.length

    if (isFocused) {
      let newIndex = newActiveIndex
      if (newActiveIndex < 0) {
        newIndex = totalSlides - 1
      } else if (newActiveIndex >= totalSlides) {
        newIndex = 0
      }
      this.setState(
        {
          activeSlide: newIndex,
          thumbnailStyle: this.getThumbnailStyleToCenter(newIndex)
        },
        this.props.onSlideChange(newIndex)
      )
    }
  }

  handleArrowClick = direction => {
    const { thumbnailStyle } = this.state
    const { componentDimension, thumbnailDimension, thumbnailSpacing } = this.getCurrentDimensions()
    const { isPortrait } = this.props
    const newThumbnailStyle = this.getThumbnailStyleAfterSlide(
      direction,
      thumbnailStyle,
      isPortrait,
      componentDimension,
      thumbnailDimension,
      thumbnailSpacing
    )

    this.setState({
      thumbnailStyle: newThumbnailStyle
    })
  }

  shouldShowArrows = () => {
    const { structuredMedia, isInModal } = this.props
    const { componentDimension, thumbnailDimension, thumbnailSpacing } = this.getCurrentDimensions()
    const numThumbnails = structuredMedia.length
    const firstLastMarginRemoved = 10
    const value = isInModal
      ? componentDimension < numThumbnails * (thumbnailDimension + thumbnailSpacing) - firstLastMarginRemoved
      : componentDimension < numThumbnails * (thumbnailDimension + thumbnailSpacing)
    return value
  }

  getNewSlideIndex = (currentSlide, key) => {
    if (key === 'ArrowRight') {
      return currentSlide + 1
    }
    if (key === 'ArrowLeft') {
      return currentSlide - 1
    }
    return currentSlide
  }

  setActiveSlide = index => {
    const { activeSlide } = this.state
    if (index !== activeSlide) {
      const thumbnailStyle = this.getThumbnailStyleToCenter(index)
      this.setState(
        {
          activeSlide: index,
          thumbnailStyle
        },
        this.props.onSlideChange(index)
      )
    }
  }

  getCurrentDimensions = () => {
    const componentWidth = this.containerRef.current.clientWidth
    const componentHeight = this.containerRef.current.clientHeight
    const { thumbnailWidth, thumbnailHeight, thumbnailSpacing } = this.state
    const { isPortrait } = this.props
    const thumbnailDimension = isPortrait ? thumbnailHeight : thumbnailWidth
    const componentDimension = isPortrait ? componentHeight : componentWidth

    return {
      componentDimension,
      thumbnailDimension,
      thumbnailSpacing
    }
  }

  getThumbnailDimensionTemplate = style => {
    switch (style) {
      case STYLE_PORTRAIT:
        return config.image.sizes.portrait
      case STYLE_LANDSCAPE:
      default:
        return config.image.sizes.default
    }
  }

  boundValidThumbnailStyle = (style, isPortrait) => {
    // If mobile, the offset of the container should always be 0 as the
    // container is scrollable, an offset will hide thumbnails outside of this
    if (!this.screenSm.matches) {
      return { top: 0, left: 0 }
    }

    const { structuredMedia } = this.props
    const numThumbnails = structuredMedia.length

    const { componentDimension, thumbnailDimension, thumbnailSpacing } = this.getCurrentDimensions()

    let { top, left } = style

    const min = -(numThumbnails * (thumbnailDimension + thumbnailSpacing) - componentDimension)
    const max = 0

    if (isPortrait) {
      top = Math.min(max, Math.max(min, top))
      left = 0
    } else {
      top = 0
      left = Math.min(max, Math.max(min, left))
    }

    return { top, left }
  }

  getThumbnailStyleToCenter = (activeSlide, isPortrait = this.props.isPortrait) => {
    const { thumbnailStyle } = this.state

    const { componentDimension, thumbnailDimension, thumbnailSpacing } = this.getCurrentDimensions()

    const thumbnailOffset = componentDimension / 2 - (activeSlide + 0.5) * (thumbnailSpacing + thumbnailDimension)
    if (!isPortrait) {
      thumbnailStyle.left = thumbnailOffset
    } else {
      thumbnailStyle.top = thumbnailOffset
    }

    return this.boundValidThumbnailStyle(thumbnailStyle, isPortrait)
  }

  getThumbnailStyleAfterSlide = (
    direction,
    initialStyle,
    isPortrait,
    componentDimension,
    thumbnailDimension,
    thumbnailSpacing
  ) => {
    const changeMag = Math.floor(componentDimension / thumbnailDimension) * (thumbnailDimension + thumbnailSpacing)
    const change = direction === 'forward' ? -changeMag : changeMag

    if (!isPortrait) {
      initialStyle.left += change
    } else {
      initialStyle.top += change
    }

    return this.boundValidThumbnailStyle(initialStyle, isPortrait)
  }

  validateThumbnailImage = thumbnail => {
    const isImage = /media\.4rgos\.it|i1\.adis\.ws|photos-eu\.bazaarvoice\.com/i
    return isImage.test(thumbnail)
  }

  getThumbnailDimentions = (thumbnail, structuredMedia, isPortrait = false) => {
    const isValidThumbnail = this.validateThumbnailImage(thumbnail)
    const thumbnailSrc = isValidThumbnail ? thumbnail : structuredMedia[0].thumbnail
    const imgQlt = 50
    const avifQlt = 30
    if (isPortrait)
      return {
        desktop: {
          src: `${thumbnailSrc}?w=80&h=108&qlt=${imgQlt}`,
          srcSet: `${thumbnailSrc}?w=160&h=216&qlt=${imgQlt}`,
          avifSrcSet: `${thumbnailSrc}?w=160&h=216&qlt=${avifQlt}`,
          width: 80,
          height: 108
        },
        mobile: {
          src: `${thumbnailSrc}?w=80&h=108&qlt=${imgQlt}`,
          srcSet: `${thumbnailSrc}?w=160&h=216&qlt=${imgQlt}`,
          avifSrcSet: `${thumbnailSrc}?w=160&h=216&qlt=${avifQlt}`,
          width: 80,
          height: 80
        }
      }
    return {
      desktop: {
        src: `${thumbnailSrc}?w=134&h=134&qlt=${imgQlt}`,
        srcSet: `${thumbnailSrc}?w=268&h=268&qlt=${imgQlt}`,
        avifSrcSet: `${thumbnailSrc}?w=268&h=268&qlt=${avifQlt}`,
        width: 134,
        height: 134
      },
      mobile: {
        src: `${thumbnailSrc}?w=90&h=90&qlt=${imgQlt}`,
        srcSet: `${thumbnailSrc}?w=180&h=180&qlt=${imgQlt}`,
        avifSrcSet: `${thumbnailSrc}?w=180&h=180&qlt=${avifQlt}`,
        width: 90,
        height: 90
      }
    }
  }

  renderThumbnails = () => {
    const { structuredMedia, dataTest, isPortrait, partNumber, model, gltf, productName } = this.props
    const { activeSlide, thumbnailWidth, thumbnailHeight, thumbnailSpacing } = this.state
    const iconSize = 0.5 * Math.min(thumbnailWidth, thumbnailHeight)
    const isInModal = this.props.isInModal
    const thumbnails =
      structuredMedia &&
      Object.keys(structuredMedia).length &&
      structuredMedia.map((media, key) => {
        const svgOverlay = media.type !== 'image'
        return (
          <Styled.ImageContainer
            isInModal={isInModal}
            data-test={`${dataTest}_thumbnail-${key}`}
            key={key}
            active={key === activeSlide}
            onClick={() => {
              this.setActiveSlide(key)
              Analytics.fire(key, 'thumbnail', Analytics.componentIdFromMediaSource(media.thumbnail), window)
            }}
            tabIndex={-1}
            className={key === activeSlide ? 'active' : ''}
            style={{
              width: thumbnailWidth,
              height: thumbnailHeight,
              margin: thumbnailSpacing / 2
            }}
          >
            <picture>
              <source
                media='(max-width: 767px)'
                type='image/webp'
                srcSet={`${
                  this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).mobile.srcSet
                }&fmt=webp`}
                width={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).mobile.width}
                height={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).mobile.height}
              />
              <source
                media='(min-width: 768px)'
                type='image/webp'
                srcSet={`${
                  this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.srcSet
                }&fmt=webp`}
                width={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.width}
                height={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.height}
              />
              <source
                media='(max-width: 767px)'
                type='image/jpeg'
                srcSet={`${
                  this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).mobile.srcSet
                }&fmt=pjpeg`}
                width={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).mobile.width}
                height={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).mobile.height}
              />
              <source
                media='(min-width: 768px)'
                type='image/jpeg'
                srcSet={`${
                  this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.srcSet
                }&fmt=pjpeg`}
                width={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.width}
                height={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.height}
              />
              <Styled.Image
                svgOverlay={svgOverlay}
                src={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.src}
                width={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.width}
                height={this.getThumbnailDimentions(media.thumbnail, structuredMedia, isPortrait).desktop.height}
                loading='lazy'
                alt='Thumbnail'
              />
            </picture>
            {media.type === '360' && (
              <Styled.ThumbnailIcon>
                <ThreeSixtyWithTextIcon width={iconSize} height={iconSize} fill='#333' />
              </Styled.ThumbnailIcon>
            )}
            {media.type === 'video' && (
              <Styled.ThumbnailIcon>
                <VideoWithTextIcon width={iconSize} height={iconSize} fill='#333' />
              </Styled.ThumbnailIcon>
            )}
            {media.type === 'inAction' && (
              <Styled.ThumbnailIcon>
                <ActionIcon width={iconSize} height={iconSize} fill='#333' />
              </Styled.ThumbnailIcon>
            )}
            {media.type === '3D-AR' && (
              <Styled.ThumbnailAR3DIcon>
                <ArkitThumbnail gltf={gltf} productName={productName} model={model} partNumber={partNumber} />
              </Styled.ThumbnailAR3DIcon>
            )}
            {media.type === 'swatch' && (
              <Styled.ThumbnailIcon>
                <SwatchIcon width={thumbnailWidth} height={thumbnailHeight} fill='#333' />
              </Styled.ThumbnailIcon>
            )}
          </Styled.ImageContainer>
        )
      })
    return thumbnails
  }

  render() {
    const { isPortrait, dataTest } = this.props
    const { thumbnailStyle, thumbnailTransition, showArrows } = this.state
    return (
      <Styled.Container
        isPortrait={isPortrait}
        data-test={isPortrait ? `${dataTest}_portrait` : `${dataTest}_landscape`}
      >
        {showArrows && (
          <Styled.CarouselArrows
            setDarkArrows={this.props.isInModal}
            onClick={() => this.handleArrowClick('back')}
            data-test={`${dataTest}_carouselArrow_back`}
          >
            {isPortrait ? <ChevronIcon dir='up' /> : <ChevronIcon dir='left' />}
          </Styled.CarouselArrows>
        )}
        <Styled.Thumbnails isPortrait={isPortrait} ref={this.containerRef}>
          <Styled.ThumbnailItem
            isPortrait={isPortrait}
            style={{
              ...thumbnailStyle,
              transition: thumbnailTransition
            }}
          >
            {this.renderThumbnails()}
          </Styled.ThumbnailItem>
        </Styled.Thumbnails>
        {showArrows && (
          <Styled.CarouselArrows
            setDarkArrows={this.props.isInModal}
            back={false}
            onClick={() => this.handleArrowClick('forward')}
            data-test={`${dataTest}_carouselArrow_forward`}
          >
            {isPortrait ? <ChevronIcon dir='down' /> : <ChevronIcon dir='right' />}
          </Styled.CarouselArrows>
        )}
      </Styled.Container>
    )
  }
}

Thumbnails.propTypes = {
  structuredMedia: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      src: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
      thumbnail: PropTypes.string.isRequired
    })
  ).isRequired,
  style: PropTypes.string,
  activeSlide: PropTypes.number,
  isPortrait: PropTypes.bool,
  onSlideChange: PropTypes.func,
  isFocused: PropTypes.bool
}

Thumbnails.defaultProps = {
  style: 'landscape',
  activeSlide: 0,
  isPortrait: false,
  onSlideChange: noop
}

export { Thumbnails }
export default withCookies(Thumbnails)
